/**
 * Global Header — sticky navbar with mega menu dropdown.
 * All content from WordPress CMS. Design follows the reference.
 *
 * Navigation UX (added on top of the CMS-driven structure):
 *   - Active-page highlighting: `isPathActive` / `isMenuItemActive` compare
 *     the current router `pathname` against each item's `url` (including
 *     descendants, so a parent nav item lights up while on any of its
 *     child pages). Applied to the desktop top-level items, every mega
 *     menu link, and every mobile drill-down level.
 *   - Themed hover: every menu link transitions its text color to
 *     `var(--color-primary)` (the CMS design-system primary color) via
 *     the Tailwind arbitrary-value utility `hover:text-[var(--color-primary)]`
 *     with a `transition-colors` duration, so hover always matches
 *     whatever brand color the CMS has configured.
 *   - Close-before-navigate: `handleMenuLinkClick` intercepts a plain
 *     left-click (no modifier keys, so ctrl/cmd/middle-click still open a
 *     new tab natively), closes the mega menu / mobile menu — which
 *     triggers each panel's Framer Motion exit animation via
 *     `AnimatePresence` — and only calls `navigate()` after
 *     `MENU_CLOSE_MS`, so the dropdown/mobile panel visibly closes before
 *     the route changes instead of vanishing under the new page.
 */

import { memo, useState, useRef, useEffect, useCallback, lazy, Suspense } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { apiClient } from '../../api/client';
import { useHeader } from '../../hooks/useHeader';
import { useTheme } from '../../providers/ThemeProvider';
// OfficeMap pulls in react-simple-maps + d3 (heavy). It only renders inside
// the Contact mega-menu on hover, so lazy-load it to keep those libs OUT of
// the main/homepage bundle. No visual change — the map still appears exactly
// as before when the Contact menu opens.
const OfficeMap = lazy(() => import('./OfficeMap'));
import { stripHtmlToText } from '../../lib/articleContent';
import type { NavItem, MegaMenuColumn, MegaMenuItem } from '../../types/header';

/** Duration (ms) the mega menu / mobile menu close animation gets to run
 * before the route actually changes. Kept in sync with the `transition`
 * durations used by the `motion.div` exit variants below. */
const MENU_CLOSE_MS = 220;

/** Strip query string, hash, and a trailing slash so `/services/` and
 * `/services?x=1#y` both normalize to `/services`. */
function normalizePath(url: string | undefined | null): string {
  if (!url) return '';
  const withoutHash = url.split('#')[0] ?? '';
  const withoutQuery = withoutHash.split('?')[0] ?? '';
  if (withoutQuery === '/') return '/';
  return withoutQuery.replace(/\/+$/, '');
}

/** True when `pathname` is on `url`'s page or a descendant route of it
 * (e.g. `/services` is active while on `/services/aws-cloud`). External
 * links, empty hrefs, and `#` anchors never count as active. */
function isPathActive(url: string | undefined | null, pathname: string): boolean {
  if (!url || url === '#' || /^https?:\/\//.test(url)) return false;
  const item = normalizePath(url);
  const current = normalizePath(pathname);
  if (item === '') return false;
  if (item === '/') return current === '/';
  return current === item || current.startsWith(`${item}/`);
}

/** True when the top-level nav item itself, or any of its (possibly
 * nested) mega-menu children, matches the current path — so a parent
 * like "Services" stays highlighted while the user is on a child page
 * like `/services/cybersecurity`. */
function isMenuItemActive(item: NavItem, pathname: string): boolean {
  if (isPathActive(item.url, pathname)) return true;
  return item.children.some(
    (col) =>
      isPathActive(col.url, pathname) ||
      col.children.some((child) => isPathActive(child.url, pathname)),
  );
}

export const GlobalHeader = memo(function GlobalHeader(): JSX.Element {
  const { data } = useHeader();
  const [activeMenu, setActiveMenu] = useState<number | null>(null);
  const [activeSubItem, setActiveSubItem] = useState<number | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const pendingNavRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Scroll detection
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  // Close mega menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setActiveMenu(null);
        setActiveSubItem(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Clear any in-flight close-then-navigate timer on unmount so a route
  // change elsewhere in the app can never trigger a stray navigate() call
  // after this component is gone.
  useEffect(() => {
    return () => {
      if (pendingNavRef.current) clearTimeout(pendingNavRef.current);
    };
  }, []);

  /**
   * Close every open menu surface (desktop mega menu + mobile drawer),
   * which kicks off each panel's Framer Motion exit animation, then
   * navigate to `url` once that animation has had time to finish. This
   * is the single source of "close before navigating" for the whole
   * header — every menu link, desktop and mobile, routes through here.
   */
  const navigateWithClose = useCallback(
    (url: string) => {
      setActiveMenu(null);
      setActiveSubItem(null);
      setMobileOpen(false);
      if (pendingNavRef.current) clearTimeout(pendingNavRef.current);
      pendingNavRef.current = setTimeout(() => {
        navigate(url);
      }, MENU_CLOSE_MS);
    },
    [navigate],
  );

  /**
   * Click handler attached to every menu `<Link>`/`<button>` in the
   * header. Lets modifier-clicks (ctrl/cmd/shift/middle-click) fall
   * through to the browser's native "open in new tab" behavior instead
   * of hijacking them; for a plain left-click it prevents the default
   * immediate navigation and routes through `navigateWithClose` so the
   * menu animates shut first.
   */
  const handleMenuLinkClick = useCallback(
    (e: React.MouseEvent, url: string) => {
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey ||
        !url ||
        url === '#' ||
        /^https?:\/\//.test(url)
      ) {
        return;
      }
      e.preventDefault();
      navigateWithClose(url);
    },
    [navigateWithClose],
  );

  if (!data) return <HeaderSkeleton />;

  const { settings, menu } = data;
  const pathname = location.pathname;

  // CMS-controlled logo width (px), independently for desktop/mobile.
  // Scaled down ~20% while `scrolled` is true to preserve the header's
  // existing "shrink slightly on scroll" effect, whatever base size the
  // CMS is configured with. Consumed by the `.cms-logo` utility class
  // (globals.css), which switches from the mobile value to the desktop
  // value at the `lg` breakpoint.
  const scrollScale = scrolled ? 0.8 : 1;
  const logoSizeVars = {
    '--logo-w-mobile': `${Math.round(settings.logoWidthMobile * scrollScale)}px`,
    '--logo-w-desktop': `${Math.round(settings.logoWidthDesktop * scrollScale)}px`,
  } as React.CSSProperties;

  return (
    <header
      ref={headerRef}
      className={`sticky top-0 z-50 w-full transition-all duration-500 ${scrolled
        ? 'py-2 px-4'
        : 'bg-white/90 dark:bg-slate-900/80 backdrop-blur-sm shadow-sm'
        }`}
    >
      {/*
        NOTE: intentionally no `border` on the rounded pill below. The
        bright seam bug was the semi-transparent light-mode border
        (border-slate-200/60) showing up against the blurred backdrop —
        visible in light mode, invisible in dark mode because the dark
        border color blends into the dark backdrop. backdrop-blur itself
        was never the problem, so it stays.
      */}
      <div
        className={`mx-auto transition-all duration-500 ${scrolled
          ? 'max-w-6xl rounded-2xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-lg shadow-lg px-4 sm:px-6'
          : 'max-w-7xl px-4 sm:px-6 lg:px-8'
          }`}
      >
        <div className={`flex items-center justify-between transition-all duration-500 ${scrolled ? 'h-14' : 'h-16'}`}>
          {/* Logo — auto switches based on dark/light mode.
              Width is CMS-controlled independently for desktop and
              mobile (`settings.logoWidthDesktop` / `logoWidthMobile`,
              in px) via the `.cms-logo` utility, which swaps at the
              `lg` breakpoint. The existing "shrink slightly while
              scrolled" effect is preserved by scaling both CMS widths
              down ~20% once `scrolled` is true, instead of the
              previous hardcoded 100px/80px pair. `height: auto` in
              `.cms-logo` keeps the logo's aspect ratio at any size. */}
          <Link to="/" className="flex-shrink-0">
            {settings.logo ? (
              <>
                <img
                  src={settings.logo}
                  alt={settings.logoAlt}
                  className="cms-logo transition-all duration-300 dark:hidden"
                  style={logoSizeVars}
                />
                <img
                  src={settings.logoDark || settings.logo}
                  alt={settings.logoAlt}
                  className="cms-logo transition-all duration-300 hidden dark:block"
                  style={logoSizeVars}
                />
              </>
            ) : (
              <span className="font-display text-xl font-bold text-slate-900 dark:text-white">{settings.logoAlt}</span>
            )}
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Main">
            {menu.map((item) => (
              <NavButton
                key={item.id}
                item={item}
                isOpen={activeMenu === item.id}
                isCurrentPage={isMenuItemActive(item, pathname)}
                onHover={() => { setActiveMenu(item.id); setActiveSubItem(item.children[0]?.id || null); }}
                onLinkClick={handleMenuLinkClick}
                scrolled={scrolled}
              />
            ))}
          </nav>

          {/* Theme Toggle + CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <ThemeSwitch />
            <Link
              to={settings.cta.url}
              className={`rounded-lg border-2 px-5 py-2 text-sm font-semibold transition-all duration-500 ${scrolled
                ? 'text-white'
                : 'text-white shadow-lg shadow-blue-500/20'
                }`}
              style={{ backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}
            >
              {settings.cta.text}
            </Link>
          </div>

          {/* Mobile: toggle + burger */}
          <div className="lg:hidden flex items-center gap-3">
            <ThemeSwitch />
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="h-10 w-10 flex items-center justify-center rounded-lg text-slate-600 dark:text-slate-300"
              aria-label="Menu"
            >
              {mobileOpen ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mega Menu Dropdown — AnimatePresence plays the exit (fade + slide-up)
          animation whenever `activeMenu` becomes null, whether that's from
          an outside click, mouse-leave, or a menu link being clicked. */}
      <AnimatePresence>
        {activeMenu && (
          <MegaMenuDropdown
            key={activeMenu}
            item={menu.find((m) => m.id === activeMenu)!}
            activeSubItem={activeSubItem}
            setActiveSubItem={setActiveSubItem}
            settings={settings}
            pathname={pathname}
            onClose={() => { setActiveMenu(null); setActiveSubItem(null); }}
            onLinkClick={handleMenuLinkClick}
            scrolled={scrolled}
          />
        )}
      </AnimatePresence>

      {/* Mobile Menu — AnimatePresence plays the panel's slide/fade exit
          before it unmounts, matching the desktop close animation. */}
      <AnimatePresence>
        {mobileOpen && (
          <MobileMenu
            menu={menu}
            cta={settings.cta}
            pathname={pathname}
            onClose={() => setMobileOpen(false)}
            onNavigate={navigateWithClose}
          />
        )}
      </AnimatePresence>
    </header>
  );
});

/* ─── Sub Components ─── */

function NavButton({
  item,
  isOpen,
  isCurrentPage,
  onHover,
  onLinkClick,
  scrolled,
}: {
  item: NavItem;
  isOpen: boolean;
  isCurrentPage: boolean;
  onHover: () => void;
  onLinkClick: (e: React.MouseEvent, url: string) => void;
  scrolled?: boolean;
}) {
  const hasDropdown = item.hasMegaMenu && item.children.length > 0;
  // The dropdown-open state and the "you are on this page" state both
  // deserve the brand color, but only the persistent active-page
  // indicator should stay lit once the dropdown closes.
  const highlighted = isOpen || isCurrentPage;
  return (
    <div onMouseEnter={onHover} className="relative">
      <Link
        to={item.url}
        onClick={(e) => onLinkClick(e, item.url)}
        aria-current={isCurrentPage ? 'page' : undefined}
        className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-300 ${highlighted
          ? ''
          : `hover:text-[var(--color-primary)] ${scrolled ? 'text-slate-700 dark:text-slate-200' : 'text-slate-700 dark:text-white'}`
          }`}
        style={highlighted ? { color: 'var(--color-primary)' } : undefined}
      >
        {item.label}
        {hasDropdown && (
          <svg className={`h-4 w-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </Link>
      {/* Underline: persistent for the active page, momentary (via isOpen)
          while its dropdown is open — both fade/scale in smoothly. */}
      <span
        aria-hidden="true"
        className={`absolute bottom-0 left-3 right-3 h-0.5 rounded-full origin-center transition-all duration-300 ${highlighted ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0'
          }`}
        style={{ backgroundColor: 'var(--color-primary)' }}
      />
    </div>
  );
}

function MegaMenuDropdown({ item, activeSubItem, setActiveSubItem, settings, pathname, onClose, onLinkClick, scrolled }: {
  item: NavItem;
  activeSubItem: number | null;
  setActiveSubItem: (id: number | null) => void;
  settings: any;
  pathname: string;
  onClose: () => void;
  onLinkClick: (e: React.MouseEvent, url: string) => void;
  scrolled?: boolean;
}) {
  if (!item.hasMegaMenu || item.children.length === 0) return null;

  // Check if this is a column-based layout (children have grandchildren = multi-column menu)
  const isColumnLayout = item.children.some((c) => c.description?.includes('[column]')) ||
    (item.children.length >= 2 && item.children.filter((c) => c.children.length > 0).length >= 2);

  // Check if this is the Contact menu
  const isContactMenu = item.label === 'Contact';

  return (
    <motion.div
      initial={{ opacity: 0, y: -12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -12, scale: 0.98 }}
      transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
      className={`absolute top-16 inset-x-0 z-50 transition-all duration-500 ${scrolled
        ? 'mx-auto mt-2 w-[min(92vw,72rem)] rounded-2xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-lg shadow-xl shadow-slate-900/10'
        : 'bg-white dark:bg-slate-900 shadow-xl shadow-slate-900/5'
        }`}
      onMouseLeave={onClose}
    >
      {/* Thin divider separating the dropdown from the header above it */}
      <div className="h-px bg-slate-300 dark:bg-slate-600" aria-hidden="true" />

      <div className={`mx-auto max-w-7xl transition-all duration-500 ${scrolled ? 'px-5 py-5' : 'px-6 py-8'}`}>
        {isColumnLayout ? (
          <ColumnLayout item={item} settings={settings} pathname={pathname} onLinkClick={onLinkClick} scrolled={scrolled} />
        ) : isContactMenu ? (
          <ContactLayout item={item} activeSubItem={activeSubItem} setActiveSubItem={setActiveSubItem} />
        ) : item.label === 'Industries' ? (
          <ListWithCardLayout item={item} settings={settings} ctaKey="industriesCta" pathname={pathname} onLinkClick={onLinkClick} scrolled={scrolled} />
        ) : item.label === 'About' ? (
          <ListWithCardLayout item={item} settings={settings} ctaKey="aboutCta" pathname={pathname} onLinkClick={onLinkClick} scrolled={scrolled} />
        ) : item.label === 'Insights' ? (
          <InsightsLayout item={item} pathname={pathname} onLinkClick={onLinkClick} />
        ) : (
          <SimpleDropdown item={item} pathname={pathname} onLinkClick={onLinkClick} />
        )}

        {/* Full-width bottom bar — same on every mega menu, CMS-managed via "Mega Menu CTA" + "Badges" tabs */}
        <MegaMenuBottomBar settings={settings} onLinkClick={onLinkClick} scrolled={scrolled} />
      </div>
    </motion.div>
  );
}

/**
 * Full-width bottom bar shown at the base of every mega menu dropdown.
 * Icon + title + description + button, all from the CMS "Mega Menu CTA"
 * tab (`settings.megaMenuCta`). This is intentionally separate from each
 * menu's own dedicated CTA card (Partners CTA, Industries CTA, About
 * CTA, etc.) — those stay exactly as they are; this bar is an additional
 * shared strip.
 */
function MegaMenuBottomBar({ settings, onLinkClick, scrolled }: { settings: any; onLinkClick: (e: React.MouseEvent, url: string) => void; scrolled?: boolean }) {
  const cta = settings.megaMenuCta;

  if (!cta) return null;

  const url = cta.buttonUrl || '/contact';

  return (
    <div className={`border-t border-slate-100 dark:border-slate-700 transition-all duration-500 ${scrolled ? 'mt-4 pt-4' : 'mt-6 pt-6'}`}>
      <div className={`flex items-center gap-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 transition-all duration-500 ${scrolled ? 'px-4 py-2.5' : 'px-5 py-3.5'}`}>
        {cta.icon && (
          <span className={`flex-shrink-0 transition-all duration-500 ${scrolled ? 'text-xl' : 'text-2xl'}`} aria-hidden="true">{cta.icon}</span>
        )}
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-bold text-slate-900 dark:text-white leading-snug">{cta.title}</h4>
          {cta.description && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed truncate">{cta.description}</p>
          )}
        </div>
        <Link
          to={url}
          onClick={(e) => onLinkClick(e, url)}
          className={`inline-flex items-center gap-1.5 text-sm font-semibold text-white rounded-lg flex-shrink-0 transition-all duration-300 hover:brightness-110 hover:scale-[1.03] ${scrolled ? 'px-3.5 py-1.5' : 'px-4 py-2'}`}
          style={{ backgroundColor: 'var(--color-primary)' }}
        >
          {cta.buttonText || 'Learn More'}
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </Link>
      </div>
    </div>
  );
}

/** Column-based layout — like "How We Help" and "Partners" */
function ColumnLayout({ item, settings, pathname, onLinkClick, scrolled }: {
  item: NavItem;
  settings: any;
  pathname: string;
  onLinkClick: (e: React.MouseEvent, url: string) => void;
  scrolled?: boolean;
}) {
  const columns = item.children.filter((c) => c.children.length > 0);
  const isPartners = item.label === 'Partners';
  const ctaData = isPartners ? settings.partnersCta : null;
  const expertUrl = settings.megaMenuCta?.buttonUrl || '/contact';
  const partnersCtaUrl = ctaData?.buttonUrl || '/partners/apply';

  return (
    <div className={`grid transition-all duration-500 ${scrolled ? 'gap-5' : 'gap-8'} ${columns.length === 1 ? 'grid-cols-[1fr_1.2fr]' : columns.length >= 3 ? 'grid-cols-[1fr_1fr_1.2fr]' : 'grid-cols-[1fr_1fr_1.2fr]'}`}>
      {/* Menu columns — each gets a right-side divider (except the last,
          which sits next to the CTA card) so the sections read clearly. */}
      {columns.map((col, i) => (
        <div
          key={col.id}
          className={i < columns.length - 1 ? 'border-r border-slate-300 dark:border-slate-600 pr-6' : ''}
        >
          <p className={`text-xs font-bold uppercase tracking-wider transition-all duration-500 ${scrolled ? 'mb-2.5' : 'mb-4'}`} style={{ color: 'var(--color-primary)' }}>
            {col.label}
          </p>
          <ul className={`transition-all duration-500 ${scrolled ? 'space-y-2' : 'space-y-3'}`} role="list">
            {col.children.map((child) => {
              const active = isPathActive(child.url, pathname);
              return (
                <li key={child.id}>
                  <Link
                    to={child.url}
                    onClick={(e) => onLinkClick(e, child.url)}
                    aria-current={active ? 'page' : undefined}
                    className={`flex items-center gap-3 text-sm transition-colors duration-300 group py-1 ${active
                      ? 'font-semibold'
                      : 'text-slate-700 dark:text-slate-300 hover:text-[var(--color-primary)]'
                      }`}
                    style={active ? { color: 'var(--color-primary)' } : undefined}
                  >
                    {child.image ? (
                      /* Logo + text together */
                      <>
                        <img src={child.image} alt={child.label} title={child.label} className={`w-auto object-contain flex-shrink-0 transition-all duration-500 ${scrolled ? 'h-5 sm:h-6' : 'h-6 sm:h-7'}`} />
                        <span className="group-hover:translate-x-0.5 transition-transform duration-300">{child.label}</span>
                      </>
                    ) : (
                      <>

                        <span className="group-hover:translate-x-0.5 transition-transform duration-300">{child.label}</span>
                      </>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      {/* CTA card — light style for Partners, dark for How We Help */}
      {isPartners && ctaData ? (
        ctaData.bgImage ? (
          /* Image-only card when bg image is uploaded */
          <Link
            to={partnersCtaUrl}
            onClick={(e) => onLinkClick(e, partnersCtaUrl)}
            className={`block rounded-2xl overflow-hidden relative h-full transition-all duration-500 hover:scale-[1.015] ${scrolled ? 'min-h-[120px]' : 'min-h-[180px]'}`}
          >
            <img src={ctaData.bgImage} alt={ctaData.title} className="w-full h-full object-cover rounded-2xl" />
          </Link>
        ) : (
          /* Text card when no image */
          <div className={`rounded-2xl bg-slate-100 dark:bg-slate-800 flex flex-col items-center justify-center text-center relative overflow-hidden transition-all duration-500 ${scrolled ? 'p-5' : 'p-8'}`}>

            <h3 className="text-slate-900 dark:text-white font-bold text-lg">{ctaData.title}</h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-2 max-w-xs">{ctaData.description}</p>
            <Link
              to={partnersCtaUrl}
              onClick={(e) => onLinkClick(e, partnersCtaUrl)}
              className="mt-4 inline-flex items-center text-sm font-semibold text-white rounded-lg px-5 py-2.5 transition-all duration-300 hover:brightness-110 hover:scale-[1.03]"
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              {ctaData.buttonText}
            </Link>
          </div>
        )
      ) : !isPartners ? (
        <div className={`rounded-2xl bg-slate-900 dark:bg-slate-800 flex flex-col justify-center relative overflow-hidden transition-all duration-500 ${scrolled ? 'p-4' : 'p-6'}`}>
          <div className="absolute top-4 right-4 text-5xl opacity-20">💬</div>
          <h3 className="text-white font-bold text-lg leading-snug">
            Need help choosing the right solution?
          </h3>
          <p className="text-slate-300 text-sm mt-2 leading-relaxed">
            Our cloud architects are ready to design a strategy that fits your unique business landscape.
          </p>
          <Link
            to={expertUrl}
            onClick={(e) => onLinkClick(e, expertUrl)}
            className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-white border border-white/30 rounded-lg px-4 py-2 transition-colors duration-300 hover:bg-white/10 w-fit"
          >
            Talk to an Expert
          </Link>
        </div>
      ) : null}
    </div>
  );
}

/** Contact menu layout — left sidebar + right office map */
function ContactLayout({ item, activeSubItem, setActiveSubItem }: { item: NavItem; activeSubItem: number | null; setActiveSubItem: (id: number | null) => void }) {
  const activeChild = item.children.find((c) => c.id === activeSubItem) || item.children[0];
  const showMap = activeChild?.label?.toLowerCase().includes('office');

  return (
    <div className="grid grid-cols-[280px_1fr] gap-6">
      {/* Left sidebar */}
      <div className="space-y-1 border-r border-slate-100 dark:border-slate-700 pr-6">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-4">{item.label}</p>
        {item.children.map((child) => (
          <button
            key={child.id}
            onMouseEnter={() => setActiveSubItem(child.id)}
            className={`w-full flex items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors ${activeSubItem === child.id ? 'bg-slate-100 dark:bg-slate-800' : 'hover:bg-slate-50 dark:hover:bg-slate-800'
              }`}
          >
            <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: activeSubItem === child.id ? 'var(--color-primary)' : '#94a3b8' }} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900 dark:text-white" style={activeSubItem === child.id ? { color: 'var(--color-primary)' } : undefined}>
                {child.label}
              </p>
              {child.description && !child.description.includes('[') && (
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{child.description}</p>
              )}
            </div>
            <svg className="h-4 w-4 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        ))}
      </div>

      {/* Right panel */}
      <div>
        {showMap ? (
          <Suspense fallback={<div className="h-full" />}>
            <OfficeMap />
          </Suspense>
        ) : (
          <div className="flex items-center justify-center h-full text-slate-400 text-sm">
            Hover a service on the left to see details
          </div>
        )}
      </div>
    </div>
  );
}

/** Insights layout — left menu + right latest 2 blog cards */
function InsightsLayout({ item, pathname, onLinkClick }: {
  item: NavItem;
  pathname: string;
  onLinkClick: (e: React.MouseEvent, url: string) => void;
}) {
  const [latestPosts, setLatestPosts] = useState<any[]>([]);

  useEffect(() => {
    apiClient.get('/wp-json/wp/v2/posts?per_page=2&_embed')
      .then((res: any) => {
        if (res.data && res.data.length > 0) {
          setLatestPosts(res.data.map((post: any) => ({
            title: stripHtmlToText(post.title.rendered),
            excerpt: stripHtmlToText(post.excerpt.rendered).slice(0, 80),
            url: `/blog/${post.slug}`,
            image: post._embedded?.['wp:featuredmedia']?.[0]?.source_url || '',
          })));
        }
      })
      .catch(() => { });
  }, []);

  return (
    <div className="grid grid-cols-[180px_1fr] gap-8">
      {/* Left: menu items */}
      <div>
        <p className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: 'var(--color-primary)' }}>
          {item.label}
        </p>
        <ul className="space-y-2.5">
          {item.children.map((child) => {
            const active = isPathActive(child.url, pathname);
            return (
              <li key={child.id}>
                <Link
                  to={child.url}
                  onClick={(e) => onLinkClick(e, child.url)}
                  aria-current={active ? 'page' : undefined}
                  className={`flex items-center gap-3 text-sm transition-colors duration-300 group py-0.5 ${active
                    ? 'font-semibold'
                    : 'text-slate-700 dark:text-slate-300 hover:text-[var(--color-primary)]'
                    }`}
                  style={active ? { color: 'var(--color-primary)' } : undefined}
                >

                  <span className="group-hover:translate-x-0.5 transition-transform duration-300 font-medium">{child.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Right: 2 latest blog cards side by side */}
      <div className="grid grid-cols-2 gap-4">
        {latestPosts.map((post, i) => (
          <Link
            key={i}
            to={post.url}
            onClick={(e) => onLinkClick(e, post.url)}
            className="block rounded-xl overflow-hidden relative h-[190px] group"
          >
            <div className="absolute inset-0 bg-slate-900">
              {post.image && (
                <img src={post.image} alt="" className="w-full h-full object-cover opacity-50 group-hover:opacity-60 transition-opacity" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-slate-900/20" />
            </div>
            <div className="relative z-10 p-5 flex flex-col justify-end h-full">
              <span className="inline-block w-fit text-[10px] font-semibold text-white px-2 py-0.5 rounded mb-2" style={{ backgroundColor: 'var(--color-primary)' }}>
                Latest Insight
              </span>
              <h4 className="text-white font-bold text-sm leading-snug line-clamp-2">{post.title}</h4>
              {post.excerpt && (
                <p className="text-slate-300 text-xs mt-1 line-clamp-2">{post.excerpt}</p>
              )}
            </div>
          </Link>
        ))}
        {latestPosts.length === 0 && (
          <div className="col-span-2 rounded-xl bg-slate-100 dark:bg-slate-800 h-[190px] flex items-center justify-center text-sm text-slate-400">
            No blog posts yet
          </div>
        )}
      </div>
    </div>
  );
}

/** List + CTA Card layout — for Industries, About */
function ListWithCardLayout({ item, settings, ctaKey, pathname, onLinkClick, scrolled }: {
  item: NavItem;
  settings: any;
  ctaKey: string;
  pathname: string;
  onLinkClick: (e: React.MouseEvent, url: string) => void;
  scrolled?: boolean;
}) {
  const cta = settings[ctaKey] || settings.industriesCta || {};
  const leafItems = item.children.filter((c) => c.children.length === 0);
  const columnItems = item.children.filter((c) => c.children.length > 0);
  const ctaUrl = cta.buttonUrl || '/contact';

  return (
    <div className={`grid transition-all duration-500 ${scrolled ? 'gap-5' : 'gap-8'} ${columnItems.length > 0 ? 'grid-cols-[1fr_1fr_1.2fr]' : 'grid-cols-[1fr_1.5fr]'}`}>
      {/* Left: flat item list */}
      <div className={columnItems.length > 0 ? 'border-r border-slate-300 dark:border-slate-600 pr-6' : ''}>
        <p className={`text-xs font-bold uppercase tracking-wider transition-all duration-500 ${scrolled ? 'mb-2.5' : 'mb-4'}`} style={{ color: 'var(--color-primary)' }}>
          {item.label}
        </p>
        <ul className={`transition-all duration-500 ${scrolled ? 'space-y-1.5' : 'space-y-2.5'}`}>
          {leafItems.map((child) => {
            const active = isPathActive(child.url, pathname);
            return (
              <li key={child.id}>
                <Link
                  to={child.url}
                  onClick={(e) => onLinkClick(e, child.url)}
                  aria-current={active ? 'page' : undefined}
                  className={`flex items-center gap-3 text-sm transition-colors duration-300 group py-0.5 ${active
                    ? 'font-semibold'
                    : 'text-slate-700 dark:text-slate-300 hover:text-[var(--color-primary)]'
                    }`}
                  style={active ? { color: 'var(--color-primary)' } : undefined}
                >

                  <span className="group-hover:translate-x-0.5 transition-transform duration-300 font-medium">{child.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Middle: sub-columns if any (e.g., "Life at Operisoft") */}
      {columnItems.length > 0 && (
        <div className="border-r border-slate-300 dark:border-slate-600 pr-6">
          {columnItems.map((col) => (
            <div key={col.id}>
              <p className={`text-xs font-bold uppercase tracking-wider transition-all duration-500 ${scrolled ? 'mb-2.5' : 'mb-4'}`} style={{ color: 'var(--color-primary)' }}>
                {col.label}
              </p>
              <ul className={`transition-all duration-500 ${scrolled ? 'space-y-1.5' : 'space-y-2.5'}`}>
                {col.children.map((sub) => {
                  const active = isPathActive(sub.url, pathname);
                  return (
                    <li key={sub.id}>
                      <Link
                        to={sub.url}
                        onClick={(e) => onLinkClick(e, sub.url)}
                        aria-current={active ? 'page' : undefined}
                        className={`flex items-center gap-3 text-sm transition-colors duration-300 group py-0.5 ${active
                          ? 'font-semibold'
                          : 'text-slate-700 dark:text-slate-300 hover:text-[var(--color-primary)]'
                          }`}
                        style={active ? { color: 'var(--color-primary)' } : undefined}
                      >

                        <span className="group-hover:translate-x-0.5 transition-transform duration-300 font-medium">{sub.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Right: Light CTA card */}
      <div className={`rounded-2xl bg-slate-100 dark:bg-slate-800 flex flex-col justify-center relative overflow-hidden transition-all duration-500 ${scrolled ? 'p-5' : 'p-8'}`}>

        <h3 className={`text-slate-900 dark:text-white font-bold leading-snug transition-all duration-500 ${scrolled ? 'text-lg' : 'text-xl'}`}>
          {cta.title || 'Learn More'}
        </h3>
        <p className={`text-slate-600 dark:text-slate-400 text-sm leading-relaxed max-w-sm transition-all duration-500 ${scrolled ? 'mt-2' : 'mt-3'}`}>
          {cta.description || ''}
        </p>
        <Link
          to={ctaUrl}
          onClick={(e) => onLinkClick(e, ctaUrl)}
          className={`inline-flex items-center gap-2 text-sm font-semibold text-white rounded-lg px-5 py-2.5 w-fit transition-all duration-300 hover:brightness-110 hover:scale-[1.03] ${scrolled ? 'mt-3' : 'mt-5'}`}
          style={{ backgroundColor: 'var(--color-primary)' }}
        >
          {cta.buttonText || 'Learn More'}
        </Link>
      </div>
    </div>
  );
}

/** Simple dropdown for menus with just a list of children */
function SimpleDropdown({ item, pathname, onLinkClick }: {
  item: NavItem;
  pathname: string;
  onLinkClick: (e: React.MouseEvent, url: string) => void;
}) {
  const colCount = Math.min(item.children.length, 4);
  return (
    <div className={`grid gap-3 ${colCount <= 2 ? 'grid-cols-2' : colCount === 3 ? 'grid-cols-3' : 'grid-cols-4'}`}>
      {item.children.map((child) => {
        const active = isPathActive(child.url, pathname);
        return (
          <Link
            key={child.id}
            to={child.url}
            onClick={(e) => onLinkClick(e, child.url)}
            aria-current={active ? 'page' : undefined}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors duration-300 group ${active
              ? 'bg-slate-50 dark:bg-slate-800 font-semibold'
              : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-[var(--color-primary)]'
              }`}
            style={active ? { color: 'var(--color-primary)' } : undefined}
          >

            <span className="group-hover:translate-x-0.5 transition-transform duration-300 font-medium">{child.label}</span>
          </Link>
        );
      })}
    </div>
  );
}

function SubMenuColumn({ column }: { column: MegaMenuColumn }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {column.image ? (
          <img src={column.image} alt={column.label} className="h-5 w-5 object-contain rounded" />
        ) : null}
        <h4 className="text-sm font-bold" style={{ color: 'var(--color-primary)' }}>{column.label}</h4>
      </div>
      <ul className="space-y-2">
        {column.children.map((item) => (
          <li key={item.id}>
            <Link
              to={item.url}
              className="text-sm text-slate-600 dark:text-slate-300 hover:text-[var(--color-primary)] transition-colors flex items-center gap-1.5"
            >
              <span className="h-1 w-1 rounded-full bg-slate-300 dark:bg-slate-600 flex-shrink-0" />
              {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MobileMenu({ menu, cta, pathname, onClose, onNavigate }: {
  menu: NavItem[];
  cta: any;
  pathname: string;
  onClose: () => void;
  onNavigate: (url: string) => void;
}) {
  const [openId, setOpenId] = useState<number | null>(null);
  const [subOpenId, setSubOpenId] = useState<number | null>(null);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Get active menu for drill-down
  const activeItem = openId ? menu.find((m) => m.id === openId) : null;
  const activeSubItem = subOpenId && activeItem ? activeItem.children.find((c: any) => c.id === subOpenId) : null;

  /** Navigate + close, letting the panel's exit animation play first
   * (same `navigateWithClose` timing contract as the desktop menu). */
  function goTo(url: string): void {
    onNavigate(url);
  }

  return (
    <motion.div
      className="lg:hidden fixed inset-0 z-[100]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* Backdrop */}
      <motion.div
        className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      />

      {/*
        Panel — slides from right, and slides back out + fades on close.
        Sized to its OWN content (header + nav list + CTA), capped at the
        viewport height, rather than force-stretched to fill the full
        screen. The previous `flex-1` on the nav list always grew to
        consume every remaining pixel down to a fixed-height CTA footer,
        so a short top-level menu (e.g. 5 items) left a large dead white
        gap between the last item and the CTA button. Dropping the forced
        grow lets the drawer hug its content when short; `max-h-[100dvh]`
        + `overflow-y-auto` on the nav list still makes longer drill-down
        levels scroll correctly without ever pushing the CTA off-screen.
      */}
      <motion.div
        className="absolute top-0 right-0 max-h-[100dvh] w-full sm:w-[380px] bg-white dark:bg-slate-900 shadow-2xl flex flex-col"
        initial={{ x: '100%', opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: '100%', opacity: 0 }}
        transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200/60 dark:border-slate-700/60">
          <span className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            {activeItem ? (activeSubItem ? activeSubItem.label : activeItem.label) : 'Menu'}
          </span>
          <div className="flex items-center gap-2">
            {(activeItem || activeSubItem) && (
              <button
                onClick={() => { if (activeSubItem) setSubOpenId(null); else setOpenId(null); }}
                className="h-9 w-9 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-300 transition-colors duration-300 hover:text-[var(--color-primary)]"
                aria-label="Back"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                </svg>
              </button>
            )}
            <button
              onClick={onClose}
              className="h-9 w-9 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-300 transition-colors duration-300 hover:text-[var(--color-primary)]"
              aria-label="Close"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content — sized to fit the actual nav item count; only scrolls
            (via the min-h-0 + overflow-y-auto combo, needed because the
            flex default is `min-height: auto` which otherwise ignores
            overflow) when a drill-down level genuinely has more items
            than fit on screen. Never force-grows to fill empty space. */}
        <div className="min-h-0 overflow-y-auto px-5 py-4">
          {/* Level 1: Main menu items */}
          {!activeItem && (
            <div className="space-y-1">
              {menu.map((item) => {
                const active = isMenuItemActive(item, pathname);
                return (
                  <button
                    key={item.id}
                    onClick={() => item.children.length > 0 ? setOpenId(item.id) : goTo(item.url)}
                    aria-current={active ? 'page' : undefined}
                    className={`w-full flex items-center justify-between p-4 rounded-xl transition-colors duration-300 group ${active ? 'bg-slate-50 dark:bg-slate-800/60' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`text-[15px] font-semibold transition-colors duration-300 ${active ? '' : 'text-slate-900 dark:text-white group-hover:text-[var(--color-primary)]'}`}
                        style={active ? { color: 'var(--color-primary)' } : undefined}
                      >
                        {item.label}
                      </span>
                    </div>
                    {item.children.length > 0 && (
                      <svg className="h-4 w-4 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-colors duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Level 2: Sub-items (children).
              Description text is only shown here for the "Contact" menu,
              mirroring the desktop `ContactLayout` sidebar — every other
              menu's desktop layout (`ListWithCardLayout`, `SimpleDropdown`,
              `InsightsLayout`, `ColumnLayout`) renders leaf items as a
              plain label with no subtext, so mobile must match that
              instead of showing every item's raw CMS description. */}
          {activeItem && !activeSubItem && (
            <motion.div
              key={activeItem.id}
              className="space-y-1"
              initial={{ x: 24, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            >
              {activeItem.children.map((child: any) => {
                const active = isPathActive(child.url, pathname) ||
                  (child.children ?? []).some((deep: any) => isPathActive(deep.url, pathname));
                const showDescription = activeItem.label === 'Contact';
                return (
                  <button
                    key={child.id}
                    onClick={() => {
                      if (child.children && child.children.length > 0) {
                        setSubOpenId(child.id);
                      } else {
                        goTo(child.url);
                      }
                    }}
                    aria-current={active ? 'page' : undefined}
                    className={`w-full flex items-center justify-between p-3.5 rounded-xl transition-colors duration-300 group ${active ? 'bg-slate-50 dark:bg-slate-800/60' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      {child.image ? (
                        <img src={child.image} alt={child.label} className="w-7 h-7 object-contain rounded" />
                      ) : null}
                      <div className="text-left">
                        <span
                          className={`text-[14px] font-medium block transition-colors duration-300 ${active ? '' : 'text-slate-900 dark:text-white group-hover:text-[var(--color-primary)]'}`}
                          style={active ? { color: 'var(--color-primary)' } : undefined}
                        >
                          {child.label}
                        </span>
                        {showDescription && child.description && !child.description.startsWith('[') && (
                          <span className="text-[12px] text-slate-500 dark:text-slate-400">{child.description}</span>
                        )}
                      </div>
                    </div>
                    {child.children && child.children.length > 0 && (
                      <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </motion.div>
          )}

          {/* Level 3: Deep children */}
          {activeSubItem && activeSubItem.children && (
            <motion.div
              key={activeSubItem.id}
              className="space-y-1"
              initial={{ x: 24, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            >
              {(activeSubItem.children as any[]).map((deep: any) => {
                const active = isPathActive(deep.url, pathname);
                return (
                  <button
                    key={deep.id}
                    onClick={() => goTo(deep.url)}
                    aria-current={active ? 'page' : undefined}
                    className={`w-full flex items-center gap-3 p-3.5 rounded-xl text-left transition-colors duration-300 group ${active ? 'bg-slate-50 dark:bg-slate-800/60' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                      }`}
                  >
                    {deep.image ? (
                      <img src={deep.image} alt={deep.label} className="w-6 h-6 object-contain rounded" />
                    ) : null}
                    <span
                      className={`text-[14px] font-medium transition-colors duration-300 ${active ? '' : 'text-slate-900 dark:text-white group-hover:text-[var(--color-primary)]'}`}
                      style={active ? { color: 'var(--color-primary)' } : undefined}
                    >
                      {deep.label}
                    </span>
                  </button>
                );
              })}
            </motion.div>
          )}
        </div>

        {/* Bottom CTA */}
        <div className="p-5 border-t border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900">
          <button
            onClick={() => goTo(cta.url)}
            className="block w-full text-center rounded-xl text-white py-3.5 font-semibold text-sm shadow-lg transition-all duration-300 hover:scale-[1.02] hover:brightness-110"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            {cta.text}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/** Apple-style dark/light toggle switch */
function ThemeSwitch() {
  const { resolvedTheme, toggleTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  return (
    <button
      onClick={toggleTheme}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      className="relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      style={{ backgroundColor: isDark ? 'var(--color-primary)' : '#e2e8f0' }}
    >
      {/* Sliding dot */}
      <span
        className={`inline-flex h-5 w-5 items-center justify-center rounded-full bg-white shadow-sm transition-transform duration-300 ${isDark ? 'translate-x-8' : 'translate-x-1'}`}
      >
        {isDark ? (
          <svg className="h-3 w-3 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
        ) : (
          <svg className="h-3 w-3 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
        )}
      </span>
    </button>
  );
}

function HeaderSkeleton() {
  return (
    <header className="sticky top-0 z-50 w-full bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
      <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
        <div className="h-8 w-32 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
        <div className="h-8 w-48 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
      </div>
    </header>
  );
}

export default GlobalHeader;
